import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const Card = styled.div`
  background: #ffffff;
  border-radius: 20px;
  padding: 60px 40px;
  max-width: 600px;
  width: 100%;
  box-shadow: 0 10px 40px rgba(247, 147, 26, 0.2);
  text-align: center;
`;

const Title = styled.h1`
  color: #f7931a;
  font-size: 2.5em;
  margin-bottom: 20px;
  font-weight: 700;
`;

const Subtitle = styled.p`
  color: #666;
  font-size: 1.1em;
  margin-bottom: 40px;
  line-height: 1.6;
`;

const Username = styled.div`
  color: #1a1a1a;
  font-size: 1.5em;
  font-weight: 600;
  margin-bottom: 30px;
  padding: 20px;
  background: rgba(247, 147, 26, 0.1);
  border-radius: 12px;
  border: 2px solid rgba(247, 147, 26, 0.3);
`;

const GenerateButton = styled.button`
  background: linear-gradient(135deg, #f7931a 0%, #ff9500 100%);
  color: white;
  border: none;
  padding: 18px 40px;
  font-size: 1.2em;
  font-weight: 600;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(247, 147, 26, 0.4);
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(247, 147, 26, 0.5);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const StatusMessage = styled.div<{ $isError?: boolean }>`
  margin-top: 30px;
  padding: 20px;
  border-radius: 12px;
  background: ${props => props.$isError ? 'rgba(255, 0, 0, 0.1)' : 'rgba(247, 147, 26, 0.1)'};
  color: ${props => props.$isError ? '#cc0000' : '#f7931a'};
  font-weight: 500;
  line-height: 1.6;
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 0.8s linear infinite;
  margin-left: 10px;
  vertical-align: middle;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const FreeBadge = styled.div`
  display: inline-block;
  background: linear-gradient(135deg, #4CAF50 0%, #45a049 100%);
  color: white;
  padding: 8px 20px;
  border-radius: 20px;
  font-weight: 600;
  font-size: 0.9em;
  margin-bottom: 30px;
  box-shadow: 0 2px 10px rgba(76, 175, 80, 0.3);
`;

const InfoBox = styled.div`
  background: rgba(247, 147, 26, 0.05);
  border-left: 4px solid #f7931a;
  padding: 20px;
  margin: 30px 0;
  text-align: left;
  border-radius: 8px;
  
  h3 {
    color: #f7931a;
    margin-bottom: 10px;
    font-size: 1.1em;
  }
  
  ul {
    color: #666;
    line-height: 1.8;
    padding-left: 20px;
  }
`;

const getBackendUrl = (path: string) => {
  if (process.env.NODE_ENV === 'production') {
    return `https://proofofprice.com${path}`;
  }
  return `http://localhost:3001${path}`;
};

const GenerateWrapped: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isGenerating, setIsGenerating] = useState(false);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  
  const username = searchParams.get('user');
  const token = searchParams.get('token');

  useEffect(() => {
    if (!username || !token) {
      setError('Missing required parameters. Please try logging in again.');
    }
  }, [username, token]);

  const handleGenerate = async () => {
    if (!username || !token) return;

    setIsGenerating(true);
    setError('');
    setStatus('Checking your tweet history...');

    try {
      // Step 1: Download tweets (if not already done)
      setStatus('Downloading your Bitcoin prediction tweets...');
      const downloadResponse = await fetch(getBackendUrl('/api/wrapped/download-tweets'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ x_handle: username })
      });

      if (!downloadResponse.ok) {
        const errorData = await downloadResponse.json();
        throw new Error(errorData.error || 'Failed to download tweets');
      }

      const downloadData = await downloadResponse.json();
      setStatus(`Found ${downloadData.tweet_count || 0} tweets. Generating your wrapped...`);

      // Step 2: Generate wrapped
      const generateResponse = await fetch(getBackendUrl('/api/wrapped/generate'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ x_handle: username })
      });

      if (!generateResponse.ok) {
        const errorData = await generateResponse.json();
        throw new Error(errorData.error || 'Failed to generate wrapped');
      }

      setStatus('Success! Redirecting to your wrapped...');
      
      // Redirect to wrapped after 2 seconds
      setTimeout(() => {
        navigate(`/wrapped?user=${username}`);
      }, 2000);

    } catch (err: any) {
      console.error('Generation error:', err);
      setError(err.message || 'Something went wrong. Please try again.');
      setIsGenerating(false);
    }
  };

  if (!username || !token) {
    return (
      <Container>
        <Card>
          <Title>⚠️ Invalid Link</Title>
          <Subtitle>
            This link is invalid or has expired. Please go back to the wrapped page and sign in again.
          </Subtitle>
        </Card>
      </Container>
    );
  }

  return (
    <Container>
      <Card>
        <FreeBadge>✨ FREE FOR INFLUENCERS</FreeBadge>
        
        <Title>🎁 Generate Your Bitcoin Wrapped</Title>
        
        <Subtitle>
          As one of our featured influencers, you get FREE access to your personalized Bitcoin Wrapped!
        </Subtitle>

        <Username>@{username}</Username>

        <InfoBox>
          <h3>What happens next?</h3>
          <ul>
            <li>We'll download your Bitcoin-related tweets</li>
            <li>Analyze your predictions and market calls</li>
            <li>Generate your personalized wrapped visualization</li>
            <li>This usually takes 30-60 seconds</li>
          </ul>
        </InfoBox>

        <GenerateButton onClick={handleGenerate} disabled={isGenerating}>
          {isGenerating ? (
            <>
              Generating Your Wrapped
              <LoadingSpinner />
            </>
          ) : (
            '🚀 Generate My Wrapped (FREE)'
          )}
        </GenerateButton>

        {status && !error && (
          <StatusMessage>{status}</StatusMessage>
        )}

        {error && (
          <StatusMessage $isError>{error}</StatusMessage>
        )}
      </Card>
    </Container>
  );
};

export default GenerateWrapped;
